import React, { useEffect, useMemo, useState } from 'react';
import { Lock, Eye, EyeOff, ShieldCheck } from 'lucide-react';
import { useAuth } from '../auth/AuthContext';
import { validateLoginFields } from '../lib/loginValidation';
import { beginCentralLogin, describeCentralLoginError, loadCentralConfig } from '../lib/centralAuth';
import PublicWhatsAppSupportButton from './PublicWhatsAppSupportButton.jsx';

export default function LoginView({ onLoggedIn, initialEmail = '', whatsappSupport }) {
  const auth = useAuth();
  const [mode, setMode] = useState('login'); // 'login' | 'forgot_password'
  const [form, setForm] = useState({ email: initialEmail, password: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [resetLoading, setResetLoading] = useState(false);
  const [notice, setNotice] = useState(null);
  const [centralAvailable, setCentralAvailable] = useState(false);
  const [centralLoading, setCentralLoading] = useState(false);

  useEffect(() => {
    let mounted = true;
    loadCentralConfig()
      .then((config) => { if (mounted) setCentralAvailable(Boolean(config)); })
      .catch(() => { if (mounted) setCentralAvailable(false); });
    return () => { mounted = false; };
  }, []);

  const onCentralLogin = async () => {
    if (centralLoading) return;
    setNotice(null);
    setCentralLoading(true);
    try {
      // Redireciona o navegador; nada executa depois em caso de sucesso.
      await beginCentralLogin();
    } catch (err) {
      setNotice({ type: 'error', text: describeCentralLoginError(err) });
      setCentralLoading(false);
    }
  };

  const canSubmit = useMemo(() => {
    const e = String(form.email || '').trim();
    if (mode === 'forgot_password') return !!e && !resetLoading;
    const p = String(form.password || '');
    return !!e && !!p && !loading;
  }, [form.email, form.password, loading, mode, resetLoading]);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (mode === 'forgot_password') {
      return onForgotPassword();
    }
    setNotice(null);

    const validation = validateLoginFields(form);
    setErrors(validation.errors);
    if (!validation.ok) return;

    setLoading(true);
    try {
      const { session } = await auth.login({
        email: validation.email,
        password: validation.password,
      });
      setForm((prev) => ({ ...prev, password: '' }));
      setErrors({});
      if (onLoggedIn) onLoggedIn(session);
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('sofera:navigate', { detail: { view: 'admin_dashboard' } }));
      }
    } catch (err) {
      const code = String(err?.code || '');
      const rawMsg = String(err?.message || '').toLowerCase();
      if (code === 'rate_limit') {
        setNotice({ type: 'error', text: 'Muitas tentativas. Tente novamente mais tarde.' });
      } else if (code === 'email_not_registered') {
        setNotice({ type: 'error', text: 'E-mail não cadastrado. Procure o RH da sua empresa para solicitar seu acesso.' });
      } else if (code === 'invalid_credentials' || rawMsg.includes('invalid login credentials')) {
        setNotice({ type: 'error', text: 'E-mail ou senha inválidos.' });
      } else if (code === 'email_not_confirmed') {
        setNotice({ type: 'error', text: 'Seu e-mail ainda não foi confirmado. Verifique sua caixa de entrada para confirmá-lo antes de entrar.' });
      } else if (code === 'user_banned') {
        setNotice({ type: 'error', text: 'Esta conta está suspensa. Entre em contato com o suporte.' });
      } else if (code === 'network_error') {
        setNotice({ type: 'error', text: 'Falha de conexão com o servidor. Tente novamente.' });
      } else if (code === 'invalid_session') {
        setNotice({ type: 'error', text: 'Sessão inválida detectada. Tente entrar novamente.' });
      } else {
        setNotice({ type: 'error', text: String(err?.message || err || 'Falha ao autenticar.') });
      }
    } finally {
      setLoading(false);
    }
  };

  const onForgotPassword = async () => {
    if (mode === 'login') {
      setMode('forgot_password');
      setNotice(null);
      return;
    }

    setNotice(null);
    setResetLoading(true);
    try {
      const redirectTo = (typeof window !== 'undefined' && window.location?.origin)
        ? `${window.location.origin}/reset`
        : undefined;
      await auth.requestPasswordReset({ email: form.email, redirectTo });
      setNotice({ type: 'success', text: 'Se este e-mail estiver cadastrado, enviaremos um link para redefinir sua senha.' });
      setMode('login');
    } catch (err) {
      const code = err?.code || '';
      if (code === 'validation') {
        setErrors(err?.fields || {});
      } else if (code === 'rate_limit') {
        setNotice({ type: 'error', text: 'Muitas tentativas. Tente novamente mais tarde.' });
      } else {
        setNotice({ type: 'error', text: String(err?.message || err || 'Falha ao solicitar redefinição de senha.') });
      }
    } finally {
      setResetLoading(false);
    }
  };

  return (
    <>
      <div className="min-h-[calc(100vh-72px)] flex items-center justify-center px-4 py-12">
        <div className="w-full max-w-md">
          <div className="text-center mb-6">
            <span className="bg-gradient-to-r from-orange-500 to-amber-400 bg-clip-text text-transparent font-black text-3xl tracking-tighter">SOFERA</span>
            <span className="text-slate-300 font-bold text-xs tracking-[0.2em] ml-2">| Evolução Inteligente</span>
          </div>
          <div className="bg-slate-900/70 border border-slate-800 rounded-2xl shadow-2xl p-6 md:p-8">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-11 h-11 rounded-xl bg-orange-500/10 border border-orange-500/20 flex items-center justify-center text-orange-400">
              <Lock size={20} />
            </div>
            <div className="flex flex-col">
              <h1 className="text-xl font-black text-white leading-tight">
                {mode === 'login' ? 'Entrar' : 'Recuperar Senha'}
              </h1>
              <p className="text-sm text-slate-400">
                {mode === 'login' ? 'Acesse com suas credenciais.' : 'Informe seu e-mail para receber o link.'}
              </p>
            </div>
          </div>

          {notice?.text && (
            <div className={`mb-4 rounded-lg border px-3 py-2 text-sm ${
              notice.type === 'success'
                ? 'border-teal-500/30 bg-teal-500/10 text-teal-200'
                : 'border-rose-500/30 bg-rose-500/10 text-rose-200'
            }`}>
              {notice.text}
            </div>
          )}

          <form onSubmit={onSubmit} className="space-y-4">
            <div>
              <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">E-mail</label>
              <input
                type="email"
                value={form.email}
                onChange={(e) => setForm((p) => ({ ...p, email: e.target.value }))}
                className={`w-full px-4 py-3 rounded-xl bg-slate-950/70 border text-slate-100 placeholder:text-slate-600 outline-none transition ${errors.email ? 'border-rose-500/40 focus:border-rose-500' : 'border-slate-800 focus:border-orange-500'}`}
                placeholder="voce@empresa.com"
                autoComplete="email"
              />
              {errors.email && <div className="mt-2 text-xs text-rose-300">{errors.email}</div>}
            </div>

            {mode === 'login' && (
              <div>
                <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">Senha</label>
                <div className="relative">
                  <input
                    type={showPassword ? "text" : "password"}
                    value={form.password}
                    onChange={(e) => setForm((p) => ({ ...p, password: e.target.value }))}
                    className={`w-full px-4 py-3 rounded-xl bg-slate-950/70 border text-slate-100 placeholder:text-slate-600 outline-none transition pr-12 ${errors.password ? 'border-rose-500/40 focus:border-rose-500' : 'border-slate-800 focus:border-orange-500'}`}
                    placeholder="********"
                    autoComplete="current-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-500 hover:text-slate-300 transition-colors"
                  >
                    {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                  </button>
                </div>
                {errors.password && <div className="mt-2 text-xs text-rose-300">{errors.password}</div>}
                <div className="mt-3 flex justify-end">
                  <button
                    type="button"
                    onClick={onForgotPassword}
                    disabled={resetLoading}
                    className="text-xs text-orange-500 hover:text-orange-400 font-bold underline disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {resetLoading ? 'Enviando...' : 'Esqueci minha senha'}
                  </button>
                </div>
              </div>
            )}

            <div className="pt-2 space-y-3">
              <button
                type="submit"
                disabled={!canSubmit}
                className="w-full h-11 rounded-xl bg-orange-500 text-white font-black hover:bg-orange-600 transition disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {mode === 'login' 
                  ? (loading ? 'Entrando...' : 'Entrar') 
                  : (resetLoading ? 'Enviando...' : 'Enviar Link de Recuperação')
                }
              </button>

              {mode === 'forgot_password' && (
                <button
                  type="button"
                  onClick={() => setMode('login')}
                  className="w-full text-xs text-slate-400 hover:text-slate-300 font-bold transition"
                >
                  Voltar para o login
                </button>
              )}
            </div>
          </form>

          {mode === 'login' && centralAvailable && (
            <>
              <div className="flex items-center gap-3 my-5">
                <span className="h-px flex-1 bg-slate-800" />
                <span className="text-[11px] font-bold uppercase tracking-wider text-slate-500">ou</span>
                <span className="h-px flex-1 bg-slate-800" />
              </div>
              <button
                type="button"
                onClick={onCentralLogin}
                disabled={centralLoading}
                className="w-full h-11 rounded-xl border border-slate-800 bg-slate-950/70 text-slate-100 font-bold hover:border-orange-500/60 hover:text-white transition flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ShieldCheck size={18} className="text-orange-400" />
                {centralLoading ? 'Redirecionando...' : 'Entrar com Acesso Único'}
              </button>
              <p className="mt-3 text-center text-xs text-slate-500">
                Use sua conta corporativa A2Care.
              </p>
            </>
          )}
          </div>
        </div>
      </div>
      <PublicWhatsAppSupportButton
        whatsappNumber={whatsappSupport?.number}
        enabled={Boolean(whatsappSupport?.enabled)}
        message="Olá! Preciso de suporte para acessar a plataforma SOFERA."
      />
    </>
  );
}
